import {
  ChangeDetectorRef,
  Component,
  Input,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { NgClickOutsideDirective } from 'ng-click-outside2';

/** Space between the trigger and the menu, in px. */
const MENU_GAP = 6;

/** Rows a consumer projects that the keyboard can land on. */
const ITEM_SELECTOR =
  '.bk-menu__item, .dropDownItems > *, [role="menuitem"], button, a[href]';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [NgClass, NgClickOutsideDirective],
  templateUrl: './dropdown.component.html',
})
export class DropdownComponent implements AfterViewInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  @Input() right?: string; // Optional right position for inline style
  @Input() left?: string; // Optional left position for inline style
  @Input() className: string = '';
  @ViewChild('triggerElement', { static: false }) triggerElement?: ElementRef;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu?: ElementRef;

  openOptions: boolean = false;
  dropdownTop: number = 0;
  dropdownLeft: number | null = null;
  dropdownRight: number | null = null;
  // Hide the menu until it has been positioned so it never flashes at the
  // top-left corner (0,0) on the first open before coordinates are computed.
  positioned: boolean = false;
  /**
   * The menu is moved to <body>, outside the app's `.dark` wrapper, so it
   * carries the theme class itself; otherwise the bk-* tokens fall back to light.
   */
  isDark = false;
  /** Opened above the trigger because there was no room below. */
  openUp = false;
  /** Anchored by its right edge, so it should grow from the right corner. */
  get anchoredRight(): boolean {
    return this.right != null && this.right !== '';
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (!this.openOptions) return;
    const hadFocus = !!this.portaledMenu?.contains(document.activeElement);
    this.closeOptions();
    if (hadFocus) this.focusTrigger();
  }

  private portaledMenu: HTMLElement | null = null;
  private reposToggleListening = false;

  // Re-anchor the (body-portaled, position:fixed) menu to its trigger whenever the
  // page or any ancestor scroll container scrolls / the window resizes. Without
  // this the menu stays pinned to its original viewport coordinates while the
  // trigger cell scrolls away, leaving it detached from both the trigger and cell.
  private reposition = () => {
    if (!this.openOptions) return;
    const trigger = this.triggerElement?.nativeElement as HTMLElement | undefined;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      // Trigger scrolled fully out of the viewport — close instead of leaving the
      // menu floating detached over unrelated content.
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        this.closeOptions();
        return;
      }
    }
    this.calculateDropdownPosition();
  };

  ngAfterViewInit() {
    // Initial position calculation will happen when dropdown opens
  }

  ngOnDestroy() {
    this.portaledMenu?.remove();
    this.portaledMenu = null;
    this.stopRepositionListening();
  }

  toggleOPenOptions() {
    this.openOptions = !this.openOptions;

    if (this.openOptions) {
      this.positioned = false;
      this.isDark = !!(this.triggerElement?.nativeElement as HTMLElement | undefined)?.closest('.dark');
      // Wait for the browser to lay out the just-rendered menu, then teleport,
      // position, and reveal it — all before the next paint, so it never flashes
      // unpositioned. requestAnimationFrame is more reliable than setTimeout(0)
      // here because the menu is guaranteed laid out by the time it runs.
      requestAnimationFrame(() => {
        if (!this.openOptions) return;
        this.teleportMenuToBody();
        this.calculateDropdownPosition();
        this.positioned = true;
        this.startRepositionListening();
        this.prepareItems();
        // Render the reveal first: a visibility:hidden item cannot take focus.
        this.cdr.detectChanges();
        this.items()[0]?.focus();
      });
    } else {
      this.positioned = false;
      this.stopRepositionListening();
    }
    this.syncTriggerState();
  }

  closeOptions() {
    this.openOptions = false;
    this.positioned = false;
    // Angular's @if will remove the element from the DOM (body) automatically
    this.portaledMenu?.removeEventListener('keydown', this.onMenuKeydown);
    this.portaledMenu = null;
    this.stopRepositionListening();
    this.syncTriggerState();
  }

  // ── Keyboard (WAI-ARIA menu button) ──
  // Opening focuses the first item; arrows, Home and End move between items;
  // Escape and Tab close and hand focus back to the trigger.

  private items(): HTMLElement[] {
    const menu = this.portaledMenu;
    if (!menu) return [];
    return Array.from(menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
      (el) =>
        // Only the outermost match, so a button inside a row is not a second stop.
        !el.parentElement?.closest(ITEM_SELECTOR) &&
        !el.hasAttribute('disabled') &&
        el.getAttribute('aria-disabled') !== 'true' &&
        !el.classList.contains('bk-menu__sep')
    );
  }

  /** Rows projected as plain divs get a role and become focusable. */
  private prepareItems() {
    for (const el of this.items()) {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'menuitem');
      el.setAttribute('tabindex', '-1');
    }
    this.portaledMenu?.addEventListener('keydown', this.onMenuKeydown);
  }

  private onMenuKeydown = (event: KeyboardEvent) => {
    const items = this.items();
    if (!items.length) return;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => items[(i + items.length) % items.length]?.focus();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index < 0 ? items.length - 1 : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(items.length - 1);
        break;
      case 'Tab':
        // The menu sits at the end of <body>; Tab from it would leave the page.
        event.preventDefault();
        this.closeOptions();
        this.focusTrigger();
        break;
      case 'Enter':
      case ' ': {
        // Native buttons and links click themselves; projected divs do not.
        const target = event.target as HTMLElement;
        if (index >= 0 && !target.matches('button, a[href]')) {
          event.preventDefault();
          target.click();
        }
        break;
      }
    }
  };

  private triggerControl(): HTMLElement | null {
    const wrapper = this.triggerElement?.nativeElement as HTMLElement | undefined;
    return (
      wrapper?.querySelector<HTMLElement>(':scope > div button, :scope > div [role="button"], :scope > div a[href]') ??
      null
    );
  }

  private focusTrigger() {
    this.triggerControl()?.focus();
  }

  /** The projected trigger announces the menu and whether it is open. */
  private syncTriggerState() {
    const control = this.triggerControl();
    if (!control) return;
    if (!control.hasAttribute('aria-haspopup')) control.setAttribute('aria-haspopup', 'menu');
    control.setAttribute('aria-expanded', String(this.openOptions));
  }

  private startRepositionListening() {
    if (this.reposToggleListening) return;
    this.reposToggleListening = true;
    // Capture phase so scrolls inside nested scroll containers (which don't bubble)
    // still trigger a re-anchor.
    window.addEventListener('scroll', this.reposition, true);
    window.addEventListener('resize', this.reposition);
  }

  private stopRepositionListening() {
    if (!this.reposToggleListening) return;
    this.reposToggleListening = false;
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
  }

  onClickedOutside(e: Event) {
    this.closeOptions();
  }

  // Move the dropdown menu to document.body so it is never constrained by
  // ancestor overflow:hidden / stacking-context containers (e.g. the data-table
  // card), ensuring it always renders above all other content.
  private teleportMenuToBody() {
    // Prefer the ViewChild, but fall back to a DOM query off the (always-present)
    // trigger wrapper in case the query hasn't resolved yet on the first open.
    const menuEl =
      (this.dropdownMenu?.nativeElement as HTMLElement | undefined) ??
      (this.triggerElement?.nativeElement?.querySelector(
        '.dropDownSection'
      ) as HTMLElement | undefined);
    if (menuEl) {
      this.portaledMenu = menuEl;
      document.body.appendChild(menuEl);
    }
  }

  private calculateDropdownPosition() {
    // Anchor off the trigger element, which is always rendered (it lives outside
    // the @if). The previous version also required the menu ViewChild, which
    // isn't resolved on the first open — so it bailed out and the menu rendered
    // at (0,0) in the top-left corner.
    const triggerEl = this.triggerElement?.nativeElement as
      | HTMLElement
      | undefined;
    if (!triggerEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Position below the trigger element
    this.dropdownTop = triggerRect.bottom + MENU_GAP;
    this.openUp = false;

    // Horizontal: anchor by the right edge when `right` is set. Anchoring by the
    // right edge means we don't need to know the menu's width (which may not be
    // measurable yet), so the first open is positioned correctly.
    if (this.right != null && this.right !== '') {
      const rightOffset = parseFloat(this.right) || 0;
      this.dropdownRight = Math.max(
        8,
        viewportWidth - triggerRect.right - rightOffset
      );
      this.dropdownLeft = null;
    } else if (this.left != null && this.left !== '') {
      const leftOffset = parseFloat(this.left) || 0;
      this.dropdownLeft = Math.max(8, triggerRect.left + leftOffset);
      this.dropdownRight = null;
    } else {
      this.dropdownLeft = Math.max(8, triggerRect.left);
      this.dropdownRight = null;
    }

    // Flip above the trigger if it would overflow the bottom and there is more
    // room above. Uses the menu height when measurable; skipped otherwise.
    const menuHeight =
      (this.dropdownMenu?.nativeElement as HTMLElement | undefined)
        ?.offsetHeight ?? 0;
    if (menuHeight && triggerRect.bottom + MENU_GAP + menuHeight > viewportHeight) {
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      if (spaceAbove > spaceBelow && spaceAbove > menuHeight + MENU_GAP) {
        this.dropdownTop = triggerRect.top - menuHeight - MENU_GAP;
        this.openUp = true;
      }
    }
  }
}
